import React, { useEffect, useState } from 'react';
import { useLive } from '../live.jsx';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth.jsx';
import { countryName } from '../productOptions.js';
import { useI18n } from '../i18n/index.jsx';
import Marquee from './Marquee.jsx';
import { useFxBase, baseOptions, crossRates } from '../fxBase.js';
import { publicGet, metalName, formatMetalPrice, unitLabel, formatChange, formatUpdated, formatRate, formatDay } from '../metalPrices.js';

// Stock-exchange style band under the header (home, marketplace, /prices):
// one strip per enabled ticker (metals, exchange rates, marketplace
// activity), in the admin's order, each with its own label and accent.
// Scrolls continuously, pauses on hover or keyboard focus; with "reduce
// motion" each strip is a static row you can scroll sideways. Data comes
// from the public /api/tickers (our database only), reloaded every
// 5 minutes; strips with nothing to show aren't sent.

function useTickers() {
  const [strips, setStrips] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => publicGet('/tickers').then((d) => { if (alive) setStrips(d.strips || []); }).catch(() => {});
    load();
    // Polling fallback (the live event below is the main signal): a newly
    // approved product / buying request shows within a minute.
    const timer = setInterval(load, 60 * 1000);
    window.addEventListener('pme:tickers', load);
    return () => { alive = false; clearInterval(timer); window.removeEventListener('pme:tickers', load); };
  }, []);
  // Changed in Admin › Tickers / Prices: read again at once.
  useLive(['tickers', 'prices', 'listings', 'requests'], () => window.dispatchEvent(new Event('pme:tickers')));
  return strips;
}

function Change({ item }) {
  const { lang } = useI18n();
  const ch = formatChange(item, lang);
  if (!ch) return null;
  return <span className={`ticker-change is-${ch.dir}`}><span aria-hidden="true">{ch.arrow}</span> {ch.text}</span>;
}

function MetalItem({ m }) {
  const { t, lang } = useI18n();
  return (
    <span className="ticker-item">
      <span className="ticker-name">{metalName(m, lang)}</span>
      <span className="ticker-price">{formatMetalPrice(m, lang)}<span className="ticker-unit">{unitLabel(m, t)}</span></span>
      <Change item={m} />
    </span>
  );
}

function FxItem({ r }) {
  const { lang } = useI18n();
  return (
    <span className="ticker-item">
      <span className="ticker-name">{r.code}/{r.base}</span>
      <span className="ticker-price">{formatRate(r.rate, lang)}</span>
      <Change item={r} />
    </span>
  );
}

// "2 h ago" in the visitor's language.
function ago(at, lang) {
  const sec = Math.max(1, Math.round((Date.now() - new Date(at).getTime()) / 1000));
  const rtf = new Intl.RelativeTimeFormat(lang === 'en' ? 'en' : 'fr', { numeric: 'auto', style: 'short' });
  if (sec < 3600) return rtf.format(-Math.max(1, Math.round(sec / 60)), 'minute');
  if (sec < 86400) return rtf.format(-Math.round(sec / 3600), 'hour');
  return rtf.format(-Math.round(sec / 86400), 'day');
}
// 🟢 À VENDRE / FOR SALE · Aluminium Ingots A7 · Montréal, CA · il y a 2 h
// 🔴 RECHERCHÉ / WANTED · 500 MT Aluminium A7 · CIF Karachi · Membre · il y a 1 h
function ActivityItem({ a, copy }) {
  const { t, lang } = useI18n();
  const nf = new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { maximumFractionDigits: 2 });
  const unit = (u) => { if (!u) return ''; const x = t(`units.${u}`); return x.startsWith('units.') ? u : x; };
  const qty = a.quantity !== null && a.quantity !== undefined ? `${nf.format(a.quantity)}${a.unit ? ` ${unit(a.unit)}` : ''}`
    : a.moq !== null && a.moq !== undefined ? `${t('tickers.activity.moq')} ${nf.format(a.moq)}${a.moqUnit ? ` ${unit(a.moqUnit)}` : ''}` : '';
  const cat = a.category ? (lang === 'en' ? a.category.en || a.category.fr : a.category.fr || a.category.en) : '';
  const place = a.type === 'wanted'
    ? [a.incoterm, [a.port, a.country && countryName(a.country, lang)].filter(Boolean).join(', ')].filter(Boolean).join(' ')
    : [a.city, a.province ? t(`provinces.${a.province}`) : '', a.country && countryName(a.country, lang)].filter(Boolean).join(', ');
  const who = a.type === 'wanted' ? (a.buyer || t('tickers.activity.member')) : '';
  const meta = [cat, qty, place, who, a.at && ago(a.at, lang)].filter(Boolean);
  return (
    <Link className={`ticker-item ticker-link ticker-${a.type}`} to={a.url} tabIndex={copy ? -1 : undefined}>
      {a.image ? <img className="ticker-thumb" src={a.image} alt="" loading="lazy" decoding="async" /> : <span className="ticker-thumb is-empty" aria-hidden="true">{a.type === 'wanted' ? '🔎' : '📦'}</span>}
      <span className={`ticker-badge is-${a.type}`}>{a.pinned ? '📌 ' : ''}{t(`tickers.activity.${a.type}`)}</span>
      <span className="ticker-price">{lang === 'en' ? a.titleEn || a.title : a.title}</span>
      {meta.map((x, i) => <span key={i} className="ticker-meta">· {x}</span>)}
    </Link>
  );
}
// Fewer than 3 items: an invitation instead.
function FewItems({ copy }) {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <span className="ticker-item">
      <span className="ticker-price">{t('tickers.activity.few')}</span>
      <Link className="ticker-link ticker-meta" to={user ? '/account/buying-requests/new' : '/signup'} tabIndex={copy ? -1 : undefined}>· {t('tickers.activity.postNeed')} →</Link>
      <Link className="ticker-link ticker-meta" to={user ? '/account/listings/new' : '/signup'} tabIndex={copy ? -1 : undefined}>· {t('tickers.activity.addProduct')} →</Link>
    </span>
  );
}

// Reading pace in pixels per second, so a preset feels the same whatever
// the number or length of items. 'custom' = the admin's seconds per loop.
const PX_PER_SECOND = { very_slow: 22, slow: 32, medium: 48, fast: 75 };
const DEFAULT_MOTION = { metals: { direction: 'left', speed: 'medium' }, fx: { direction: 'right', speed: 'slow' }, activity: { direction: 'left', speed: 'very_slow' } };
const DEFAULT_COLOR = { metals: 'metallic', fx: 'ocean', activity: 'brand' };

// Small icons in the label pills: metal bars, exchange arrows, storefront.
function TagIcon({ code }) {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, className: 'tag-icon' };
  if (code === 'metals') {
    return <svg {...common}><path d="M3 20h8l-1.5-5h-5z" /><path d="M13 20h8l-1.5-5h-5z" /><path d="M8 13h8l-1.5-5h-5z" /></svg>;
  }
  if (code === 'fx') {
    return <svg {...common}><path d="M4 8h13" /><path d="M14 5l3 3-3 3" /><path d="M20 16H7" /><path d="M10 13l-3 3 3 3" /></svg>;
  }
  return <svg {...common}><path d="M4 10h16l-1.5-5h-13z" /><path d="M5 10v9h14v-9" /><path d="M10 19v-5h4v5" /></svg>;
}

// Base-currency menu at the start of the exchange-rate strip.
function FxBaseSelect({ items }) {
  const { t } = useI18n();
  const [base, setBase] = useFxBase();
  const options = baseOptions(items);
  return (
    <select className="ticker-base" value={options.includes(base) ? base : 'CAD'} onChange={(e) => setBase(e.target.value)} aria-label={t('tickers.fx.baseLabel')} title={t('tickers.fx.baseLabel')}>
      {options.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

function Strip({ strip, fxBase }) {
  const { t, lang } = useI18n();
  const motion = { ...DEFAULT_MOTION[strip.code], ...(strip.motion || {}) };
  const items = strip.code === 'fx' ? crossRates(strip.items, fxBase) : strip.items;
  const render = (copy) => items.map((it, i) => {
    if (strip.code === 'metals') return <MetalItem key={it.code} m={it} />;
    if (strip.code === 'fx') return <FxItem key={it.code} r={it} />;
    return <ActivityItem key={`${it.type}-${it.url}-${i}`} a={it} copy={copy} />;
  });
  const renderActivity = (copy) => (strip.few ? [<FewItems key="few" copy={copy} />] : render(copy));
  const tagLink = strip.code === 'activity' ? '/marketplace' : '/prices';
  return (
    <section className={`ticker ticker-${strip.code} theme-${strip.color || DEFAULT_COLOR[strip.code]}`} aria-label={t(`tickers.${strip.code}.label`)}>
      <div className="ticker-tag">
        {strip.code === 'fx'
          ? <div className="tag-pill"><TagIcon code="fx" /><span className="tag-word">{t('tickers.fx.tagShort')}</span><FxBaseSelect items={strip.items} /></div>
          : <Link to={tagLink} className="tag-pill"><TagIcon code={strip.code} /><span className="tag-word">{t(`tickers.${strip.code}.tag`)}</span></Link>}
      </div>
      <Marquee render={strip.code === 'activity' ? renderActivity : render} direction={motion.direction} deps={[items, lang, strip.few]}
        pxPerSecond={PX_PER_SECOND[motion.speed] || PX_PER_SECOND.medium}
        loopSeconds={motion.speed === 'custom' ? motion.loopSeconds : null} />
    </section>
  );
}

// Small information line under the strips (sources, times, disclaimers),
// scrolling slowly like the strips; wraps and stays still with
// "reduce motion".
function InfoLine({ metals, fx, fxBase }) {
  const { t, lang } = useI18n();
  const render = (copy) => [
    metals && <span key="m" className="note-item"><strong>{t('tickers.metals.noteLabel')}</strong> <Disclaimer data={metals} copy={copy} /></span>,
    fx && <span key="f" className="note-item"><strong>{t('tickers.fx.noteLabel')}</strong> {t('tickers.fx.disclaimer', { date: formatDay(fx.date, lang) })}{fxBase !== 'CAD' && ` ${t('tickers.fx.crossNote', { base: fxBase })}`}</span>,
    <span key="l" className="note-item"><Link to="/prices" tabIndex={copy ? -1 : undefined}>{t('prices.allPrices')} →</Link></span>,
  ].filter(Boolean);
  return (
    <div className="ticker-note">
      <Marquee render={render} direction="left" pxPerSecond={26} deps={[metals, fx, fxBase, lang]} label={t('tickers.noteLabel')} />
    </div>
  );
}

// Where the activity strip shows (Admin › Tickers): home, marketplace, both, or all pages.
const ACTIVITY_ON = { home: ['home'], marketplace: ['marketplace'], home_marketplace: ['home', 'marketplace'], all: ['home', 'marketplace', 'prices', 'other'] };
function Band({ page, activityOnly }) {
  const all = useTickers();
  const [fxBase] = useFxBase();
  if (!all) return null;
  const strips = all.filter((s) => (s.code === 'activity' ? (ACTIVITY_ON[s.pages] || ACTIVITY_ON.home_marketplace).includes(page) : !activityOnly));
  if (!strips.length) return null;
  const metals = strips.find((s) => s.code === 'metals');
  const fx = strips.find((s) => s.code === 'fx');
  const base = fx && baseOptions(fx.items).includes(fxBase) ? fxBase : 'CAD';
  return (
    <div className="ticker-wrap">
      {strips.map((s) => <Strip key={s.code} strip={s} fxBase={base} />)}
      {(metals || fx) && <InfoLine metals={metals} fx={fx} fxBase={base} />}
    </div>
  );
}

export function Disclaimer({ data, copy }) {
  const { t, lang } = useI18n();
  return (
    <span>
      {t('prices.disclaimer')} {t('prices.source')}{' '}
      <a href={data.source.url} target="_blank" rel="noreferrer noopener nofollow" tabIndex={copy ? -1 : undefined}>{data.source.name}</a>
      {data.updatedAt && <>, {t('prices.updated', { time: formatUpdated(data.updatedAt, lang) })}</>}.
    </span>
  );
}

// The band is decoration: if it ever fails to render, hide it rather than
// replacing the whole page with the error screen.
class TickerBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('[ticker]', err); }
  render() { return this.state.failed ? null : this.props.children; }
}

// page: home | marketplace | prices | other (other = every other public page, activity strip only).
export default function PriceTicker({ page = 'other' }) {
  return <TickerBoundary><Band page={page} activityOnly={page === 'other'} /></TickerBoundary>;
}
// On the other public pages, when the activity strip is set to "all pages".
export function ActivityStripElsewhere() {
  const { pathname } = useLocation();
  if (/^\/(|marketplace|prices|account|admin)(\/|$)/.test(pathname) && !/^\/marketplace\/./.test(pathname)) return null;
  return <PriceTicker page="other" />;
}
